import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import DocumentChatShell from '../shells/DocumentChatShell';
import AssistantBotRow from '../shells/AssistantBotRow';
import ContinueButtonBar from '../components/ContinueButtonBar';
import { useDocumentChatNavigation } from '../shells/useDocumentChatNavigation';
import DocumentChatLayout from '../layout/DocumentChatLayout';
import { useChatThreadScroll } from '../hooks/useChatThreadScroll';
import { interpolateI18n } from '../utils/interpolateI18n';
import '../layout/DocumentChatLayout.css';

const FIRST_ASSISTANT_DELAY_MS = 1000;
const FOLLOW_ASSISTANT_GAP_MS = 650;

export interface SkipFillInFormChatFlowProps {
  documentTitle: string;
  onBack: () => void;
  onContinue: () => void;
}

const SkipFillInFormChatFlow: React.FC<SkipFillInFormChatFlowProps> = ({
  documentTitle,
  onBack,
  onContinue,
}) => {
  const { t } = useLanguage();
  const { exitToChecklist } = useDocumentChatNavigation(onBack);
  const [revealPhase, setRevealPhase] = useState(0);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const greeting = useMemo(
    () => interpolateI18n(t('submitDocuments.skipFillInFormChat.greetingPrefix'), { documentTitle }),
    [t, documentTitle]
  );

  const intro = useMemo(
    () => interpolateI18n(t('submitDocuments.skipFillInFormChat.intro'), { documentTitle }),
    [t, documentTitle]
  );

  useEffect(() => {
    setRevealPhase(0);
    const t1 = window.setTimeout(() => setRevealPhase(1), FIRST_ASSISTANT_DELAY_MS);
    const t2 = window.setTimeout(
      () => setRevealPhase(2),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS
    );
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [documentTitle]);

  useChatThreadScroll(threadEndRef, [revealPhase]);

  return (
    <DocumentChatShell onBack={exitToChecklist} cancelLabel={t('submitDocuments.chat.cancelProcedure')}>
      <DocumentChatLayout
        thread={
          <>
            {revealPhase >= 1 && <AssistantBotRow animateIn>{greeting}</AssistantBotRow>}
            {revealPhase >= 2 && <AssistantBotRow animateIn>{intro}</AssistantBotRow>}
            <div ref={threadEndRef} aria-hidden />
          </>
        }
        composer={revealPhase >= 2 ? <ContinueButtonBar onContinue={onContinue} /> : null}
      />
    </DocumentChatShell>
  );
};

export default SkipFillInFormChatFlow;
